"use client";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { isHandyApp } from "../utils/device";

interface AppEnvironmentContextType {
  /** 是否在 Handy App 内 */
  isInApp: boolean;
  /** 环境检测是否完成 */
  isReady: boolean;
}

const AppEnvironmentContext = createContext<
  AppEnvironmentContextType | undefined
>(undefined);

/**
 * 检测是否为 App 环境
 * 1. 优先检查 URL 参数 (source=app)
 * 2. 检查 User Agent 中的 BBL-APP 标识
 */
function detectAppEnvironment(): boolean {
  // 检查 URL 参数
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get("source");

    // URL 参数优先级最高
    if (sourceParam === "app") {
      return true;
    }
  }

  // 检查 User Agent
  return isHandyApp();
}

/**
 * App 环境 Provider
 * 在应用加载时自动检测是否在 Flutter App 内嵌
 */
export function AppEnvironmentProvider({ children }: { children: ReactNode }) {
  const [isInApp, setIsInApp] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // 检测是否在 App 内
    const inApp = detectAppEnvironment();
    setIsInApp(inApp);
    setIsReady(true);

    // 在开发环境下打印日志
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[AppEnvironment] 环境检测完成: ${inApp ? "App 内" : "浏览器"}`,
      );
      console.log(`[AppEnvironment] User Agent: ${navigator.userAgent}`);
      console.log(`[AppEnvironment] URL: ${window.location.href}`);
    }

    // 将检测结果存储到全局，方便其他模块使用
    if (typeof window !== "undefined") {
      (window as any).__IS_HANDY_APP__ = inApp;
    }
  }, []);

  const value: AppEnvironmentContextType = {
    isInApp,
    isReady,
  };

  return (
    <AppEnvironmentContext.Provider value={value}>
      {children}
    </AppEnvironmentContext.Provider>
  );
}

/**
 * 使用 App 环境 Hook
 */
export function useAppEnvironment() {
  const context = useContext(AppEnvironmentContext);
  if (!context) {
    throw new Error(
      "useAppEnvironment must be used within AppEnvironmentProvider",
    );
  }
  return context;
}

/**
 * 检查是否在 App 内
 */
export function useIsInApp() {
  const { isInApp } = useAppEnvironment();
  return isInApp;
}

/**
 * 获取全局的 App 状态（非 React 环境使用）
 */
export function getIsInApp(): boolean {
  if (typeof window !== "undefined") {
    return (window as any).__IS_HANDY_APP__ ?? isHandyApp();
  }
  return false;
}
